// Modern asset registry using the asset service
import { PixiBundler, createPixiBundler } from './PixiBundler';
import type { GameAssetManifest } from './loaders/AssetService';

export interface LegacyGameAssetManifest {
  spine?: Record<
    string,
    { json: string; atlas: string; fallbackImage?: string }
  >;
  images?: Record<string, string>;
  sounds?: Record<string, string>;
  atlases?: Record<
    string,
    {
      json: string;
      image: string;
    }
  >;
}

export class AssetRegistry {
  private pixiBundler: PixiBundler;
  private manifest: LegacyGameAssetManifest = {};

  constructor(
    private slug: string,
    private baseUrl: string = ''
  ) {
    this.pixiBundler = createPixiBundler(slug, baseUrl);
  }

  /**
   * Register a game's asset manifest (legacy format)
   */
  registerManifest(manifest: LegacyGameAssetManifest): void {
    this.manifest = manifest;
    console.log(`[AssetRegistry] Registered manifest for '${this.slug}':`, {
      spine: Object.keys(manifest.spine || {}),
      images: Object.keys(manifest.images || {}),
      sounds: Object.keys(manifest.sounds || {}),
    });
  }

  /**
   * Initialize the asset registry
   */
  async initialize(): Promise<void> {
    // Convert legacy manifest to new format
    const newManifest: GameAssetManifest = {
      version: '1.0.0',
      assets: {
        images: this.manifest.images,
        sounds: this.manifest.sounds,
        atlases: this.manifest.atlases,
        spine: this.manifest.spine
          ? Object.fromEntries(
              Object.entries(this.manifest.spine).map(([name, config]) => [
                name,
                {
                  skeleton: config.json,
                  atlas: config.atlas,
                  scale: 1.0,
                },
              ])
            )
          : undefined,
      },
    };

    await this.pixiBundler.initialize(newManifest);
  }

  /**
   * Get spine asset paths by logical name (legacy compatibility)
   */
  getSpineAsset(
    name: string
  ): { json: string; atlas: string; fallbackImage?: string } | null {
    return this.manifest.spine?.[name] || null;
  }

  /**
   * Get image asset path by logical name (legacy compatibility)
   */
  getImageAsset(name: string): string | null {
    return this.manifest.images?.[name] || null;
  }

  /**
   * Get sound asset path by logical name (legacy compatibility)
   */
  getSoundAsset(name: string): string | null {
    return this.manifest.sounds?.[name] || null;
  }

  /**
   * Load a spine animation by logical name
   */
  async loadSpine(name: string): Promise<any> {
    console.log(
      `[AssetRegistry] Loading spine '${name}' for slug '${this.slug}'`
    );
    const spineAsset = this.getSpineAsset(name);

    if (!spineAsset) {
      throw new Error(`Spine asset '${name}' not found in manifest`);
    }

    // Extract the spine name from the path (remove .json extension)
    // FIX: Extract only the filename to avoid path duplication in AssetService
    const parts = spineAsset.json.split('/');
    const spineName = parts[parts.length - 1].replace(/\.(json|skel)$/, '');
    return this.pixiBundler.loadSpine(spineName);
  }

  /**
   * Get a loaded spine animation by logical name
   */
  getSpine(name: string): any {
    const spineAsset = this.getSpineAsset(name);
    if (!spineAsset) return null;

    // Extract the spine name from the path (remove .json extension)
    // FIX: Extract only the filename to match loadSpine
    const parts = spineAsset.json.split('/');
    const spineName = parts[parts.length - 1].replace(/\.(json|skel)$/, '');
    return this.pixiBundler.getSpine(spineName);
  }

  async loadAtlas(name: string) {
    const atlas = this.manifest.atlases?.[name];
    if (!atlas) {
      throw new Error(`Atlas '${name}' not found in manifest`);
    }

    return this.pixiBundler.loadAtlas(name, atlas.json, atlas.image);
  }

  getAtlas(name: string) {
    return this.pixiBundler.getAtlas(name);
  }

  /**
   * Load an image by logical name
   */
  async loadImage(name: string): Promise<any> {
    const imagePath = this.getImageAsset(name);
    if (!imagePath) {
      throw new Error(`Image asset '${name}' not found in manifest`);
    }

    return this.pixiBundler.loadTexture(imagePath);
  }

  /**
   * Get a loaded texture by logical name
   */
  getTexture(name: string): any {
    const imagePath = this.getImageAsset(name);
    if (!imagePath) return null;

    return this.pixiBundler.getTexture(imagePath);
  }

  /**
   * Get sound map for audio services (converts logical names to paths)
   */
  getSoundMap(): Record<string, string> {
    return this.manifest.sounds || {};
  }

  /**
   * Preload all assets defined in the manifest
   */
  async preloadAll(): Promise<void> {
    await this.pixiBundler.preloadAll();
    console.log(`[AssetRegistry] Preloaded assets for game '${this.slug}'`);
  }

  /**
   * Get the underlying pixi bundler for direct access
   */
  getPixiBundler(): PixiBundler {
    return this.pixiBundler;
  }
}
